// Sidebar navigation for the documentation site. Each slug must have a
// matching entry in `content.tsx`. Sections render in declaration order.

export interface DocLink {
  title: string;
  slug: string;
  /** Show a "soon" / "alpha" pill in the sidebar. */
  status?: "soon" | "alpha";
}

export interface DocSection {
  title: string;
  links: DocLink[];
}

export const DOC_SECTIONS: DocSection[] = [
  {
    title: "Get started",
    links: [
      { title: "Introduction", slug: "" },
      { title: "Quickstart", slug: "getting-started" },
      { title: "Installation", slug: "installation" },
      { title: "Your first project", slug: "first-project" },
    ],
  },
  {
    title: "Core concepts",
    links: [
      { title: "Projects", slug: "concepts/projects" },
      { title: "Tools", slug: "concepts/tools" },
      { title: "MCP servers", slug: "concepts/mcp" },
      { title: "Memory", slug: "concepts/memory" },
      { title: "Vector databases", slug: "concepts/vector" },
      { title: "Permissions", slug: "concepts/permissions" },
    ],
  },
  {
    title: "Connectors",
    links: [
      { title: "Catalog overview", slug: "connectors" },
      { title: "OAuth providers", slug: "connectors/oauth" },
      { title: "API key providers", slug: "connectors/api-keys" },
      { title: "Custom connectors", slug: "connectors/custom" },
    ],
  },
  {
    title: "Tools & registry",
    links: [
      { title: "Tool registry", slug: "tools" },
      { title: "Multi-protocol export", slug: "tools/export" },
      { title: "Permissions on tools", slug: "tools/permissions" },
      { title: "Versioning", slug: "tools/versioning" },
    ],
  },
  {
    title: "Memory & knowledge",
    links: [
      { title: "Memory stores", slug: "memory" },
      { title: "Vector & RAG", slug: "memory/vector" },
      { title: "Knowledge bases", slug: "memory/knowledge-bases" },
      { title: "Ingestion sources", slug: "memory/ingest" },
    ],
  },
  {
    title: "Authentication",
    links: [
      { title: "Email + password", slug: "auth/email-password" },
      { title: "OAuth setup", slug: "auth/oauth" },
      { title: "API keys", slug: "auth/api-keys" },
      { title: "Sessions", slug: "auth/sessions" },
    ],
  },
  {
    title: "Agents",
    links: [
      { title: "Agents overview", slug: "agents", status: "soon" },
      { title: "Skills (skills.sh)", slug: "skills", status: "soon" },
      { title: "Agent runtime", slug: "agents/runtime", status: "soon" },
      { title: "Scheduling", slug: "agents/scheduling", status: "soon" },
      { title: "Deployments", slug: "agents/deployments", status: "soon" },
    ],
  },
  {
    title: "APIs",
    links: [
      { title: "REST API", slug: "api/rest" },
      { title: "GraphQL API", slug: "api/graphql", status: "soon" },
      { title: "MCP API", slug: "api/mcp" },
      { title: "Webhooks", slug: "api/webhooks", status: "soon" },
    ],
  },
  {
    title: "SDKs",
    links: [
      { title: "TypeScript", slug: "sdks/typescript" },
      { title: "Python", slug: "sdks/python", status: "alpha" },
      { title: "Go", slug: "sdks/go", status: "soon" },
    ],
  },
  {
    title: "Self-hosting",
    links: [
      { title: "Overview", slug: "self-hosting" },
      { title: "Docker", slug: "self-hosting/docker" },
      { title: "Kubernetes", slug: "self-hosting/kubernetes", status: "soon" },
    ],
  },
  {
    title: "Resources",
    links: [
      { title: "Changelog", slug: "changelog" },
      { title: "About", slug: "about" },
      { title: "Privacy", slug: "privacy" },
      { title: "Terms", slug: "terms" },
    ],
  },
];

/** Flat list of all slugs (for the not-found page + sitemap). */
export const ALL_DOC_SLUGS: string[] = DOC_SECTIONS.flatMap((s) =>
  s.links.map((l) => l.slug),
);
